import React, { useState, useRef, useEffect } from 'react';
import startLogo from "./start.png"
import listening from "./listening.png"

// IMPORTANT: Create a .env file in your project's root and add your Groq API key:
// REACT_APP_GROQ_API_KEY=your_api_key_here
const GROQ_API_KEY = "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";

const App = () => {
  const [text, setText] = useState('');
  const [response, setResponse] = useState('');
  const [assistantState, setAssistantState] = useState('idle');
  const [conversation, setConversation] = useState([]);
  const [voices, setVoices] = useState([]);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(null);
  const speakingSessionId = useRef(0);
  useEffect(() => {
    document.title = "chaww chaww";
    const loadVoices = () => {
      const loadedVoices = speechSynthesis.getVoices();
      if (loadedVoices.length) {
        setVoices(loadedVoices);
      }
    };
    speechSynthesis.onvoiceschanged = loadVoices;
    loadVoices();

    const handleUnload = () => {
      speechSynthesis.cancel();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      speechSynthesis.cancel();
    };
  }, []);

  const speakText = (content) => {
    const sessionId = ++speakingSessionId.current;
    speechSynthesis.cancel(); // Stop any previous speech

    // Split by sentence endings or newlines
    let rawChunks = content.match(/[^.!?।\n]+[.!?।\n]*/g) || [content];
    
    // Further split long chunks to avoid browser limits (approx 180 chars is safe)
    const MAX_CHUNK_LENGTH = 180;
    let chunks = [];
    
    rawChunks.forEach(chunk => {
      if (chunk.length > MAX_CHUNK_LENGTH) {
        const words = chunk.split(' ');
        let currentChunk = '';
        words.forEach(word => {
          if ((currentChunk + word).length < MAX_CHUNK_LENGTH) {
            currentChunk += (currentChunk ? ' ' : '') + word;
          } else {
            if (currentChunk) chunks.push(currentChunk);
            currentChunk = word;
          }
        });
        if (currentChunk) chunks.push(currentChunk);
      } else {
        chunks.push(chunk);
      }
    });

    if (chunks.length === 0) {
      setAssistantState('idle');
      return;
    }

    let chunkIndex = 0;

    const speakNextChunk = () => {
      if (speakingSessionId.current !== sessionId) return;
      if (chunkIndex >= chunks.length) {
        setAssistantState('idle');
        return;
      }

      // Clean text: remove special characters, punctuation, emojis
      // Keep English letters/numbers, Hindi, Punjabi, and whitespace
      let textToSpeak = chunks[chunkIndex].replace(/[^a-zA-Z0-9\s\u0900-\u097F\u0A00-\u0A7F]/g, " ");
      textToSpeak = textToSpeak.replace(/\s+/g, " ").trim();

      if (!textToSpeak) {
        chunkIndex++;
        speakNextChunk();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      synthesisRef.current = utterance;
      let lang = 'en-US';
      if (/[\u0900-\u097F]/.test(textToSpeak)) {
        lang = 'hi-IN';
      } else if (/[\u0A00-\u0A7F]/.test(textToSpeak)) {
        lang = 'pa-IN';
      }
      const availableVoices = voices.length > 0 ? voices : speechSynthesis.getVoices();
      const matchedVoices = availableVoices.filter(voice => voice.lang.replace('_', '-') === lang);
      const femaleVoice = matchedVoices.find(voice =>
        voice.name.toLowerCase().includes('female') ||
        voice.name.toLowerCase().includes('zira') ||
        voice.name.toLowerCase().includes('hema')
      );
      utterance.voice = femaleVoice || matchedVoices[0] || availableVoices[0] || null;
      utterance.lang = lang;
      if (lang === 'en-US') {
        utterance.rate = 0.7;
      } else {
        utterance.rate = 0.9;
      }

      utterance.onstart = () => {
        if (chunkIndex === 0) {
          setAssistantState('speaking');
        }
      };

      utterance.onend = () => {
        chunkIndex++;
        speakNextChunk();
      };

      utterance.onerror = (event) => {
        console.error('SpeechSynthesisUtterance.onerror', event);
        chunkIndex++;
        speakNextChunk();
      };

      speechSynthesis.speak(utterance);
    };

    speakNextChunk();
  };


  const sendToApi = async (message) => {
    setAssistantState('thinking');

    if (!GROQ_API_KEY) {
      const errorMsg = "API key is missing.";
      setResponse(errorMsg);
      speakText(errorMsg);
      setAssistantState('idle');
      return;
    }

    const systemPrompt = `
You are a helpful assistant.
Always reply in the same language the user speaks.
If the user speaks in Hindi, reply in native Devanagari Hindi.
If Punjabi, reply in Gurmukhi.
If English, reply in English.
Respond in a female tone and treat the user as male if gender is not mentioned.
`;

    // Convert old conversation format → plain text
    const historyText = conversation
      .map(item =>
        `${item.role === "model" ? "Assistant" : "User"}: ${item.parts[0].text}`
      )
      .join("\n");


const finalInput =  `${systemPrompt}\n\n${historyText}\nUser: ${message}` ;


    try {
      const result = await fetch(
        "https://api.groq.com/openai/v1/responses",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${GROQ_API_KEY}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model: "openai/gpt-oss-20b",
            input: finalInput
          })
        }
      );

      const data = await result.json();

      const messageItem = data.output?.find(item => item.type === 'message');
      const reply = messageItem?.content?.[0]?.text || "No response received.";

      // Clean text: remove special characters, punctuation, emojis
      const cleanReply = reply.replace(/[^a-zA-Z0-9\s\u0900-\u097F\u0A00-\u0A7F]/g, " ").replace(/\s+/g, " ").trim();

      setResponse(cleanReply);
      speakText(reply);

      setConversation(prev => [
        ...prev,
        { role: "user", parts: [{ text: message }] },
        { role: "model", parts: [{ text: cleanReply }] }
      ]);

      setAssistantState('idle');
    } catch (error) {
      console.error("API Error:", error);
      setAssistantState('idle');
    }
  };



  const startListening = () => {
    speakingSessionId.current++;
    speechSynthesis.cancel();
    setAssistantState('idle');
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech Recognition not supported in your browser.');
      return;
    }
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-IN';
    recognitionRef.current.onresult = (event) => {
      const spokenText = event.results[0][0].transcript;
      setText(spokenText);
      if (event.results[0].isFinal) {
        sendToApi(spokenText);
      }
    };
    recognitionRef.current.onend = () => {
      setAssistantState(prev => prev === 'listening' ? 'idle' : prev);
    };
    recognitionRef.current.start();
    setAssistantState('listening');
  };
  return (
    <div style={{
      fontFamily: 'Arial',
      maxWidth: '600px',
      margin: 'auto',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '20rem', margin: '1rem' }}>
        {assistantState === 'idle' && <img src={startLogo} style={{ width: "320px", marginBottom: "-109px" }} />}
        {assistantState === 'listening' && <img src={listening} style={{ width: "320px", marginBottom: "-109px" }} />}
        {assistantState === 'thinking' && '🤔'}
        {assistantState === 'speaking' && '💁‍♀️'}
      </div>
      <button
        onClick={startListening}
        style={{
          padding: '10px 20px',
          fontSize: '1.2rem',
          cursor: 'pointer',
          borderRadius: '10px',
          background: '#007bff',
          color: 'white',
          border: 'none',
          marginBottom: '1rem'
        }}
      >
        🎤 Start Listening
      </button>
      <div style={{ textAlign: 'left', fontSize: '1.1rem', marginLeft: "25px", marginRight: "25px" }}>
        <p><strong>Koushal:</strong> {text}</p>
        <p><strong>chaww chaww:</strong> {response}</p>
      </div>
    </div>

  );
};

export default App;
